// AccessibilityContext.js
import React, { createContext, useState, useEffect } from 'react';

export const AccessibilityContext = createContext({
  selectedOption: 'standard',
  updateAccessibilityOption: () => {},
  getAccessibilityVariant: () => 'standard',
  zoomLevel: 100,
  updateZoomLevel: () => {},
  animationsEnabled: true,          // Par défaut
  updateAnimationsEnabled: () => {}
});

export const AccessibilityProvider = ({ children }) => {
  const [selectedOption, setSelectedOption] = useState(() => {
    // Vérifier si localStorage est disponible (pas en SSR)
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'standard';
    }
    const storedOption = localStorage.getItem('selectedAccessibilityOption');
    return storedOption ? storedOption : 'standard';
  });

  const [zoomLevel, setZoomLevel] = useState(() => {
    // Vérifier si localStorage est disponible (pas en SSR)
    if (typeof window === 'undefined' || !window.localStorage) {
      return 100;
    }
    const storedZoom = localStorage.getItem('zoomLevel');
    return storedZoom ? parseInt(storedZoom, 10) : 100;
  });

  // Nouvel état pour les animations
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    // Vérifier si localStorage est disponible (pas en SSR)
    if (typeof window === 'undefined' || !window.localStorage) {
      return true;
    }
    const savedValue = localStorage.getItem('animationsEnabled');
    return savedValue !== null ? JSON.parse(savedValue) : true;
  });

  useEffect(() => {
    // Mettre à jour l'accessibilité de la taille de police (seulement côté client)
    if (typeof document !== 'undefined' && document.documentElement) {
      const root = document.documentElement;
      const initialRootFontSize = 62.5;
      const newRootFontSize = (initialRootFontSize * zoomLevel) / 100;
      root.style.setProperty('--root-font-size', `${newRootFontSize}%`);
    }
  }, [zoomLevel]);

  useEffect(() => {
    // Vérifier si localStorage est disponible (pas en SSR)
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('selectedAccessibilityOption', selectedOption);
    }
  }, [selectedOption]);

  useEffect(() => {
    // Vérifier si localStorage est disponible (pas en SSR)
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('zoomLevel', zoomLevel);
    }
  }, [zoomLevel]);

  // Effet pour les animations
  useEffect(() => {
    // Vérifier si localStorage et document sont disponibles (pas en SSR)
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('animationsEnabled', JSON.stringify(animationsEnabled));
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.style.setProperty(
        '--transition',
        animationsEnabled ? 'all .2s ease-out' : 'none'
      );
    }
  }, [animationsEnabled]);

  const updateAccessibilityOption = (option) => {
    setSelectedOption(option);
  };

  const updateZoomLevel = (level) => {
    setZoomLevel(level);
  };

  const updateAnimationsEnabled = (enabled) => {
    setAnimationsEnabled(enabled);
  };

  const getAccessibilityVariant = () => {
    switch (selectedOption) {
      case 'redGreenBlindness':
        return 'redGreen';
      case 'blueYellowBlindness':
        return 'blueYellow';
      case 'achromatopsia':
        return 'blackWhite';
      case 'blackTheme':
        return 'dark';
      default:
        return 'standard';
    }
  };

  useEffect(() => {
    // Appliquer les classes d'accessibilité (seulement côté client)
    if (typeof document !== 'undefined' && document.body) {
      const variantClass = getAccessibilityVariant();
      const body = document.body;
      const accessibilityClasses = ['redGreen', 'blueYellow', 'blackWhite', 'dark'];
      body.classList.remove(...accessibilityClasses);

      if (variantClass !== 'standard') {
        body.classList.add(variantClass);
      }
    }
  }, [selectedOption]);

  return (
    <AccessibilityContext.Provider
      value={{
        selectedOption,
        updateAccessibilityOption,
        getAccessibilityVariant,
        zoomLevel,
        updateZoomLevel,
        animationsEnabled,
        updateAnimationsEnabled
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};
